/**
 * Gerenciador Global de Arraste Elástico (Rubber Band)
 * Cria a sensação de peso/mola ao tentar arrastar elementos iterativos da tela.
 * Ignora footer, parallax e elementos dentro de modais (exceto o próprio modal).
 */

export function initGlobalRubberband() {
  let isDragging = false;
  let startX = 0;
  let startY = 0;
  let dragged = false;
  let targetEl = null;
  let cloneEl = null;
  let originalTransform = '';
  let dragType = 'normal'; // 'normal', 'header', 'footer'
  let originalZIndex = '';
  let parentOriginalZIndex = '';
  let inlineBgColor = '';
  let inlineBackdropFilter = '';
  let computedBgColor = '';
  let computedBackdropFilter = '';

  // Tags que normalmente recebem interação
  const interactiveTags = ['A', 'IMG', 'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'SPAN', 'I', 'BUTTON', 'TIME', 'STRONG', 'B', 'LI', 'FIGURE', 'ARTICLE'];

  const onPointerDown = (e) => {
    // Ignora botão direito ou meio do mouse
    if (e.button !== 0 && e.button !== 2) return;
    
    // GRACEFUL DEGRADATION: Desabilita totalmente a física no celular
    // Garantindo 100% de fluidez no scroll nativo do touch.
    if (e.pointerType === 'touch') return;

    let el = e.target;

    // 1. Lógica de Modais
    const modalContent = el.closest('.modal-content');
    
    // Verifica se tocou no header ou footer
    let headerEl = el.closest('#header-wrap');
    if (!headerEl) headerEl = el.closest('header, #header, .header');
    
    const footerEl = el.closest('footer, #footer, .footer');
    
    if (modalContent || el.closest('.no-rubberband, .wolf-showcase-page, .camera-canvas-box')) {
      // Desativa totalmente o rubberband dentro de modais e páginas especiais para permitir arrasto livre e seleção sem interferência
      return;
    } else if (headerEl && !headerEl.closest('.modal-content')) {
      el = headerEl;
      dragType = 'header';
      // Garante que o celular não cancele o evento para rolar a página se o toque começou no menu
      el.style.touchAction = 'none';
    } else if (footerEl && !footerEl.closest('.modal-content')) {
      el = footerEl;
      dragType = 'footer';
      el.style.touchAction = 'none';
    } else {
      // 2. Procura um elemento interativo subindo a árvore
      let current = el;
      let found = null;
      while (current && current !== document.body) {
        if (
          interactiveTags.includes(current.tagName) ||
          current.classList.contains('icon') ||
          current.classList.contains('btn') ||
          current.classList.contains('button') ||
          current.classList.contains('date') ||
          current.classList.contains('post-date')
        ) {
          found = current;
          break;
        }
        if (
          current.classList.contains('container') ||
          current.classList.contains('row') ||
          current.classList.contains('col') ||
          current.classList.contains('wrapper') ||
          current.id === 'content'
        ) {
          break;
        }
        current = current.parentElement;
      }
      
      if (!found) return;
      el = found;
      dragType = 'normal';
    }

    // Se a intenção for scroll no mobile, não impede, a menos que ele atinja o threshold e dragType normal.

    targetEl = el;

    // Salva transform pré-existente
    originalTransform = targetEl.style.transform || '';
    originalZIndex = targetEl.style.zIndex || '';
    if (targetEl.parentElement) {
      parentOriginalZIndex = targetEl.parentElement.style.zIndex || '';
    }
    
    inlineBgColor = targetEl.style.backgroundColor || '';
    inlineBackdropFilter = targetEl.style.backdropFilter || '';
    
    const comp = window.getComputedStyle(targetEl);
    computedBgColor = comp.backgroundColor;
    computedBackdropFilter = comp.backdropFilter;

    targetEl._rubberbandAncestors = null;

    isDragging = true;
    dragged = false;
    startX = e.clientX;
    startY = e.clientY;

    window.dispatchEvent(new CustomEvent('rubberband-drag', { detail: true }));

    targetEl.style.cursor = 'grabbing';
    // O Efeito 3D será aplicado ao clone
  };

  const onPointerMove = (e) => {
    if (!isDragging || !targetEl) return;

    const dx = e.clientX - startX;
    const dy = e.clientY - startY;

    // Aumenta o limite de 3px para 10px para evitar que um pequeno tremor (jiggle) do mouse ao clicar cancele o click
    if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
      if (!dragged) {
        dragged = true;
        
        if (dragType === 'normal') {
          // GHOST DRAGGING: Clona o elemento exato para o body
          const rect = targetEl.getBoundingClientRect();
          
          targetEl._rubberbandClone = targetEl.cloneNode(true);
          cloneEl = targetEl._rubberbandClone;
          
          const freezeStyles = (src, dest) => {
            const s = window.getComputedStyle(src);
            for (let i = 0; i < s.length; i++) {
              const name = s[i];
              dest.style.setProperty(name, s.getPropertyValue(name), s.getPropertyPriority(name));
            }
          };

          freezeStyles(targetEl, cloneEl);
          
          const srcChildren = targetEl.querySelectorAll('*');
          const destChildren = cloneEl.querySelectorAll('*');
          for (let i = 0; i < srcChildren.length; i++) {
            freezeStyles(srcChildren[i], destChildren[i]);
          }
          
          cloneEl.style.setProperty('position', 'fixed', 'important');
          cloneEl.style.setProperty('top', rect.top + 'px', 'important');
          cloneEl.style.setProperty('left', rect.left + 'px', 'important');
          cloneEl.style.setProperty('width', rect.width + 'px', 'important');
          cloneEl.style.setProperty('height', rect.height + 'px', 'important');
          cloneEl.style.setProperty('margin', '0', 'important');
          cloneEl.style.setProperty('z-index', '90', 'important');
          cloneEl.style.setProperty('pointer-events', 'none', 'important');
          cloneEl.style.willChange = 'transform, scale';
          cloneEl.style.transition = 'scale 0.15s ease-out';
          cloneEl.style.scale = '1.02';
          
          document.body.appendChild(cloneEl);
          targetEl.style.opacity = '0';
        } else {
          // Para header/footer, previne o bug de sumir (Stacking Context)
          targetEl.style.willChange = 'transform, opacity, background-color';
          targetEl.style.setProperty('z-index', '999999', 'important');
          if (targetEl.parentElement) {
            targetEl.parentElement.style.setProperty('z-index', '999999', 'important');
          }
        }
      }
    }

    if (!dragged) return;
    
    // Evita o scroll do celular quando estamos ativamente arrastando
    if (e.cancelable) {
      // Mas só bloqueia se o movimento estiver a favor da restrição
      if (dragType === 'header' && dy < 0) {
        // Se tentar rolar pra cima no header, deixa rolar a página
      } else if (dragType === 'footer' && dy > 0) {
        // Se tentar rolar pra baixo no footer, deixa rolar
      } else {
        e.preventDefault();
      }
    }

    if (dragType === 'normal') {
      if (!cloneEl) return;
      const frictionX = Math.sign(dx) * Math.pow(Math.abs(dx), 0.5) * 0.6;
      const frictionY = Math.sign(dy) * Math.pow(Math.abs(dy), 0.5) * 0.6;
      cloneEl.style.transform = `${originalTransform} translate3d(${frictionX}px, ${frictionY}px, 0)`;
    } else {
      // Header ou Footer (ESTICAR em vez de arrastar)
      const progress = Math.min(Math.abs(dy) / (window.innerHeight * 0.4), 1);
      
      // scale máximo de 10% (1.10)
      let scale = 1 + (0.10 * Math.sin(progress * Math.PI / 2));
      
      if (dragType === 'header') {
        if (dy < 0) scale = 1; // Só estica pra baixo
        targetEl.style.transformOrigin = 'top center';
      } else if (dragType === 'footer') {
        if (dy > 0) scale = 1; // Só estica pra cima
        targetEl.style.transformOrigin = 'bottom center';
      }
      
      targetEl.style.transform = `${originalTransform} scaleY(${scale})`;
      
      // Manipula apenas o BACKGROUND e o BLUR, mantendo o logo e o texto 100% visíveis
      if (computedBgColor && computedBgColor !== 'rgba(0, 0, 0, 0)' && computedBgColor !== 'transparent') {
        const match = computedBgColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
        if (match) {
          const r = match[1], g = match[2], b = match[3];
          let a = match[4] !== undefined ? parseFloat(match[4]) : 1;
          a = Math.max(0, a - (a * 0.8 * progress)); // Cai em até 80%
          targetEl.style.backgroundColor = `rgba(${r}, ${g}, ${b}, ${a})`;
        }
      }

      if (computedBackdropFilter && computedBackdropFilter !== 'none') {
        const blurMatch = computedBackdropFilter.match(/blur\(([\d.]+)px\)/);
        if (blurMatch) {
          let blurVal = parseFloat(blurMatch[1]);
          blurVal = Math.max(0, blurVal - (blurVal * 0.8 * progress)); // Reduz o desfoque
          targetEl.style.backdropFilter = computedBackdropFilter.replace(/blur\([\d.]+px\)/, `blur(${blurVal}px)`);
        }
      }
    }
  };

  const onPointerUp = (e) => {
    if (!isDragging || !targetEl) return;
    isDragging = false;

    // Avisa que o arraste parou (reativa bolhas)
    window.dispatchEvent(new CustomEvent('rubberband-drag', { detail: false }));

    targetEl.style.cursor = '';

    const clone = targetEl._rubberbandClone;
    const currentOriginal = targetEl;
    const currentDragType = dragType;
    targetEl = null;

    if (currentDragType === 'normal' && clone) {
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      const frictionX = Math.sign(dx) * Math.pow(Math.abs(dx), 0.5) * 0.6;
      const frictionY = Math.sign(dy) * Math.pow(Math.abs(dy), 0.5) * 0.6;

      clone.style.scale = '1';
      clone.style.willChange = 'auto';

      if (e.type === 'pointercancel') {
        clone.remove();
        if (currentOriginal) currentOriginal.style.opacity = '';
        return;
      }

      const animation = clone.animate([
        { transform: `${originalTransform} translate3d(${frictionX}px, ${frictionY}px, 0)` },
        { transform: `${originalTransform} translate3d(${-frictionX * 0.4}px, ${-frictionY * 0.4}px, 0)`, offset: 0.4 },
        { transform: `${originalTransform} translate3d(${frictionX * 0.15}px, ${frictionY * 0.15}px, 0)`, offset: 0.7 },
        { transform: originalTransform || 'translate3d(0, 0, 0)' }
      ], { duration: 500, easing: 'ease-in-out' });
      
      animation.onfinish = () => {
        clone.remove();
        if (currentOriginal) currentOriginal.style.opacity = '';
      };
    } else if (currentDragType !== 'normal' && currentOriginal) {
      // Restaura estilos originais caso tenha sido apenas um clique
      if (!dragged) {
        currentOriginal.style.transform = originalTransform;
        currentOriginal.style.backgroundColor = inlineBgColor;
        currentOriginal.style.backdropFilter = inlineBackdropFilter;
        currentOriginal.style.willChange = 'auto';
        currentOriginal.style.touchAction = '';
        currentOriginal.style.zIndex = originalZIndex;
        if (currentOriginal.parentElement) currentOriginal.parentElement.style.zIndex = parentOriginalZIndex;
        return;
      }

      const dy = e.clientY - startY;
      let progress = Math.min(Math.abs(dy) / (window.innerHeight * 0.4), 1);
      let scale = 1 + (0.10 * Math.sin(progress * Math.PI / 2));
      
      if (currentDragType === 'header' && dy < 0) scale = 1;
      else if (currentDragType === 'footer' && dy > 0) scale = 1;

      if (e.type === 'pointercancel') {
        currentOriginal.style.transform = originalTransform;
        currentOriginal.style.backgroundColor = inlineBgColor;
        currentOriginal.style.backdropFilter = inlineBackdropFilter;
        currentOriginal.style.willChange = 'auto';
        currentOriginal.style.touchAction = '';
        currentOriginal.style.zIndex = originalZIndex;
        if (currentOriginal.parentElement) currentOriginal.parentElement.style.zIndex = parentOriginalZIndex;
        return;
      }

      const currentBg = currentOriginal.style.backgroundColor || computedBgColor;
      const currentBlur = currentOriginal.style.backdropFilter || computedBackdropFilter;

      const animation = currentOriginal.animate([
        { 
          transform: `${originalTransform} scaleY(${scale})`,
          backgroundColor: currentBg,
          backdropFilter: currentBlur
        },
        { transform: `${originalTransform} scaleY(${1 - (scale - 1) * 0.4})`, offset: 0.4 },
        { transform: `${originalTransform} scaleY(${1 + (scale - 1) * 0.15})`, offset: 0.7 },
        { 
          transform: originalTransform || 'scaleY(1)',
          backgroundColor: computedBgColor,
          backdropFilter: computedBackdropFilter
        }
      ], { duration: 500, easing: 'ease-in-out' });

      animation.onfinish = () => {
        currentOriginal.style.transform = originalTransform;
        currentOriginal.style.backgroundColor = inlineBgColor;
        currentOriginal.style.backdropFilter = inlineBackdropFilter;
        currentOriginal.style.willChange = 'auto';
        currentOriginal.style.touchAction = '';
        currentOriginal.style.zIndex = originalZIndex;
        if (currentOriginal.parentElement) currentOriginal.parentElement.style.zIndex = parentOriginalZIndex;
      };
    }
  };

  const onClick = (e) => {
    // Bloqueia clique caso tenha sido arrastado pra longe intencionalmente
    if (dragged) {
      e.preventDefault();
      e.stopPropagation();
      dragged = false;
    }
  };

  window.addEventListener('pointerdown', onPointerDown, { passive: false });
  window.addEventListener('pointermove', onPointerMove, { passive: false });
  window.addEventListener('pointerup', onPointerUp, { passive: true });
  window.addEventListener('pointercancel', onPointerUp, { passive: true });
  window.addEventListener('click', onClick, true); // fase capture para rodar antes dos originais
}
